import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtDateTime } from "@/lib/format";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function PlatformLegal() {
  const [terms, setTerms] = useState("");
  const [privacy, setPrivacy] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/platform/legal").then((r) => {
      setTerms(r.data.terms || "");
      setPrivacy(r.data.privacy || "");
      setUpdatedAt(r.data.updated_at || null);
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/platform/legal", { terms, privacy });
      toast.success("Legal documents saved");
      const r = await api.get("/platform/legal");
      setUpdatedAt(r.data.updated_at || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="platform-legal">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Platform Admin</div>
        <h1 className="font-display text-4xl sm:text-5xl">Legal Documents</h1>
        <p className="text-zinc-500 text-sm mt-2">These texts are shown on the public <Link to="/terms" target="_blank" className="text-gold underline inline-flex items-center gap-1">Terms <ExternalLink className="w-3 h-3" /></Link> and <Link to="/privacy" target="_blank" className="text-gold underline inline-flex items-center gap-1">Privacy <ExternalLink className="w-3 h-3" /></Link> pages, and linked from every audition signup form.</p>
        {updatedAt && <p className="text-zinc-600 text-xs mt-2">Last updated: {fmtDateTime(updatedAt)}</p>}
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest">Terms of Use</Label>
          <span className="text-[11px] text-zinc-500">{terms.length.toLocaleString()} chars</span>
        </div>
        <Textarea data-testid="platform-legal-terms" rows={12} value={terms} onChange={(e) => setTerms(e.target.value)} className="bg-[#121212] border-white/10 font-mono text-sm" />
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest">Privacy Policy</Label>
          <span className="text-[11px] text-zinc-500">{privacy.length.toLocaleString()} chars</span>
        </div>
        <Textarea data-testid="platform-legal-privacy" rows={12} value={privacy} onChange={(e) => setPrivacy(e.target.value)} className="bg-[#121212] border-white/10 font-mono text-sm" />
      </div>

      <div className="flex justify-end gap-2">
        <button data-testid="platform-legal-save-btn" onClick={save} disabled={saving} style={goldBtn} className="h-10 px-5 rounded font-semibold text-sm inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
